import { Controller } from "@hotwired/stimulus";
import "../../i18n/translations";

// Contrôleur Stimulus pour gérer les filtres et la recherche des communications
export default class extends Controller {
  static targets = ["searchInput", "resetBtn"];

  // Délai de debounce en millisecondes
  static values = {
    debounceDelay: { type: Number, default: 300 },
    url: String,
    errorMessage: String,
  };

  connect() {
    console.log("Communications filter controller connected");
    // Initialisation du timer pour le debounce
    this.debounceTimer = null;
    this.updateResetVisibility();
  }

  // Affiche le bouton « Réinitialiser » dès qu'un filtre est actif
  // (recherche, audience, type ou statut), comme dans le design system.
  updateResetVisibility() {
    if (!this.hasResetBtnTarget) return;
    const search = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : "";
    const audience = document.querySelector('select[name="audience"]')?.value || "";
    const type = document.querySelector('select[name="communication_type"]')?.value || "";
    const status = new URLSearchParams(window.location.search).get("status") || "";
    // display (et non l'attribut hidden) car .ds-btn impose display:inline-flex
    this.resetBtnTarget.style.display = (search || audience || type || status) ? "" : "none";
  }

  disconnect() {
    console.log("Communications filter controller disconnected");
    // Nettoyer le timer si nécessaire
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  // Méthode pour la recherche avec debounce
  search(event) {
    console.log("Search event triggered");
    // Affiche/masque immédiatement le bouton Réinitialiser
    this.updateResetVisibility();
    // Annuler tout timer existant
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Créer un nouveau timer
    this.debounceTimer = setTimeout(() => {
      this.submitSearch();
    }, this.debounceDelayValue);
  }

  // Méthode pour appliquer les filtres depuis la modal
  applyFilters(event) {
    console.log("Apply filters event triggered");
    event.preventDefault();

    // Récupérer les valeurs des filtres de la modal
    const filters = this.collectFilterValues();

    // Ajouter la valeur de recherche de l'input principal
    const searchValue = this.hasSearchInputTarget
      ? this.searchInputTarget.value.trim()
      : "";
    if (searchValue) {
      filters.search = searchValue;
    }

    // Appliquer les filtres
    this.submitWithFilters(filters);
  }

  // Méthode pour réinitialiser tous les filtres
  resetFilters(event) {
    console.log("Reset filters event triggered");
    event.preventDefault();

    // Réinitialiser le champ de recherche
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = "";
    }

    // Réinitialiser les filtres de la modal
    this.resetModalFilters();

    // Masquer le bouton Réinitialiser
    this.updateResetVisibility();

    // Soumettre sans filtres
    this.submitWithFilters({});
  }

  // Méthode pour soumettre la recherche
  submitSearch() {
    const searchValue = this.hasSearchInputTarget
      ? this.searchInputTarget.value.trim()
      : "";

    // Récupérer les filtres actuels
    const filters = this.collectFilterValues();
    filters.search = searchValue;

    this.submitWithFilters(filters);
  }

  // Méthode pour soumettre avec des filtres spécifiques
  submitWithFilters(filters) {
    // Créer l'URL avec les paramètres - utiliser l'URL actuelle comme base
    const baseUrl = this.hasUrlValue ? this.urlValue : window.location.pathname;
    const url = new URL(baseUrl, window.location.origin);

    // Ajouter tous les filtres non vides
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "") {
        url.searchParams.append(key, value);
      }
    });

    console.log("Submitting with URL:", url.toString());
    console.log("Filters:", filters);

    // Récupérer le conteneur des communications
    const communicationsContainer = document.getElementById("communications");

    // Ajouter une classe pour afficher le loader
    if (communicationsContainer) {
      communicationsContainer.classList.add("loading");
    }

    // Récupérer le CSRF token de manière sécurisée
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (!csrfToken) {
      console.error("CSRF token not found");
    }

    // Effectuer la requête avec Turbo
    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken || "",
      },
    })
      .then((response) => {
        if (response.ok) {
          return response.text();
        }
        const fallbackMessage = this.hasErrorMessageValue && this.errorMessageValue.length > 0
            ? this.errorMessageValue
            : window.t("js.communications.filter.network_error");
        throw new Error(fallbackMessage);
      })
      .then((html) => {
        Turbo.renderStreamMessage(html);
      })
      .catch((error) => {
        const fallbackMessage = window.t("js.communications.filter.network_error");
        const message = this.hasErrorMessageValue && this.errorMessageValue.length > 0
          ? this.errorMessageValue
          : fallbackMessage;
        console.error(`${window.t("js.communications.filter.console_error")} ${error.message}`, error);
        alert(message);
      })
      .finally(() => {
        // Retirer la classe loading après la requête
        if (communicationsContainer) {
          communicationsContainer.classList.remove("loading");
        }
      });
  }

  // Méthode déclenchée par les filtres inline (selects dans la page)
  submitFromInline() {
    this.updateResetVisibility();
    this.submitSearch();
  }

  // Méthode pour collecter les valeurs des filtres (inline selects ou modal)
  collectFilterValues() {
    const filters = {};
    const urlParams = new URLSearchParams(window.location.search);

    // Préserver le filtre de statut depuis l'URL
    if (urlParams.get("status")) {
      filters.status = urlParams.get("status");
    }

    // Collecter le filtre d'audience : select inline en priorité, puis radio modal, puis URL
    const audienceSelect = document.querySelector('select[name="audience"]');
    const categoryRadio = document.querySelector(
      'input[name="category"]:checked'
    );
    if (audienceSelect && audienceSelect.value) {
      filters.audience = audienceSelect.value;
    } else if (categoryRadio && categoryRadio.value) {
      filters.audience = categoryRadio.value;
    } else if (urlParams.get("audience")) {
      filters.audience = urlParams.get("audience");
    }

    // Collecter le filtre de type : select inline en priorité, puis radio modal, puis URL
    const typeSelect = document.querySelector(
      'select[name="communication_type"]'
    );
    const typeRadio = document.querySelector('input[name="type"]:checked');
    if (typeSelect && typeSelect.value) {
      filters.communication_type = typeSelect.value;
    } else if (typeRadio && typeRadio.value) {
      filters.communication_type = typeRadio.value;
    } else if (urlParams.get("communication_type")) {
      filters.communication_type = urlParams.get("communication_type");
    }

    return filters;
  }

  // Méthode pour réinitialiser les filtres (modal + inline)
  resetModalFilters() {
    // Décocher tous les boutons radio
    document.querySelectorAll('input[name="category"]').forEach((radio) => {
      radio.checked = radio.value === "";
    });

    document.querySelectorAll('input[name="type"]').forEach((radio) => {
      radio.checked = radio.value === "";
    });

    // Réinitialiser les selects inline
    const audienceSelect = document.querySelector('select[name="audience"]');
    if (audienceSelect) audienceSelect.value = "";

    const typeSelect = document.querySelector(
      'select[name="communication_type"]'
    );
    if (typeSelect) typeSelect.value = "";
  }

  // Méthodes pour être appelées depuis la modal via des événements
  handleFilterChange(event) {
    // Ne pas appliquer automatiquement, attendre le bouton "Filtrer"
    console.log("Filter changed:", event.target.name, event.target.value);
  }
}
